import { Users, Plus, Mail } from 'lucide-react'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { InviteTeamMemberDialog } from './InviteTeamMemberDialog'
import { TeamMemberRow } from './TeamMemberRow'
import { PendingInvitationRow } from './PendingInvitationRow'
import { useRouteParams } from '@/hooks/useRoutes'

interface TeamMember {
  id: string
  user_id: string
  email: string
  full_name: string
  avatar_url: string | null
  role: 'owner' | 'admin' | 'agent'
  created_at: string
}

interface PendingInvitation {
  id: string
  email: string
  role: 'admin' | 'agent'
  invited_by_name: string
  expires_at: string
  created_at: string
}

interface TeamData {
  team_members: TeamMember[]
  pending_invitations: PendingInvitation[]
  max_team_members: number | null
  can_invite: boolean
  can_manage: boolean
}

interface TeamSettingsCardProps {
  team: TeamData | null
}

export function TeamSettingsCard({ team }: TeamSettingsCardProps) {
  const { withAccount } = useRouteParams()
  const [dialogOpen, setDialogOpen] = useState(false)

  const accountParams = withAccount()

  if (!team) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Unable to load team data.</p>
        </CardContent>
      </Card>
    )
  }

  const { team_members, pending_invitations, max_team_members, can_invite, can_manage } = team
  const totalCount = team_members.length + pending_invitations.length
  const unlimited = max_team_members === null || max_team_members === -1
  const limitDisplay = unlimited ? '∞' : max_team_members

  const handleRoleChange = (memberId: string, newRole: 'admin' | 'agent') => {
    router.patch(
      `/${accountParams.account_id}/team/${memberId}`,
      { team_member: { role: newRole } },
      { preserveScroll: true }
    )
  }

  const handleRemoveMember = (memberId: string) => {
    router.delete(`/${accountParams.account_id}/team/${memberId}`, {
      preserveScroll: true,
    })
  }

  const handleRevokeInvitation = (invitationId: string) => {
    router.delete(`/${accountParams.account_id}/invitations/${invitationId}`, {
      preserveScroll: true,
    })
  }

  const handleResendInvitation = (invitationId: string) => {
    router.post(`/${accountParams.account_id}/invitations/${invitationId}/resend`, {}, {
      preserveScroll: true,
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="size-5" />
              Team Members
            </CardTitle>
            <CardDescription>
              Manage your team and invite new members to collaborate.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {totalCount} / {limitDisplay}
            </span>
            {can_manage && (
              can_invite ? (
                <Button
                  onClick={() => setDialogOpen(true)}
                  size="sm"
                  className="cursor-pointer"
                >
                  <Plus className="size-4 mr-1" />
                  Invite
                </Button>
              ) : (
                <Tooltip content={`Upgrade to invite more team members (limit: ${limitDisplay})`}>
                  <Button
                    size="sm"
                    className="cursor-pointer"
                    disabled
                  >
                    <Plus className="size-4 mr-1" />
                    Invite
                  </Button>
                </Tooltip>
              )
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Team Members */}
          <div className="space-y-2">
            {team_members.map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                canManage={can_manage}
                onRoleChange={handleRoleChange}
                onRemove={handleRemoveMember}
              />
            ))}
          </div>

          {/* Pending Invitations */}
          {pending_invitations.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Mail className="size-4" />
                Pending Invitations
              </h4>
              {pending_invitations.map((invitation) => (
                <PendingInvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  canManage={can_manage}
                  onResend={handleResendInvitation}
                  onRevoke={handleRevokeInvitation}
                />
              ))}
            </div>
          )}

          {!can_invite && can_manage && (
            <p className="text-sm text-muted-foreground mt-4">
              You've reached your team member limit ({limitDisplay}). Upgrade your plan to invite more members.
            </p>
          )}
        </div>
      </CardContent>

      <InviteTeamMemberDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </Card>
  )
}
